import { useEffect, useRef, memo } from 'react';
import { useCookieConsent, CookieConsent } from '@/hooks/useCookieConsent';

/**
 * CookieScriptLoader - Manages third-party scripts based on user consent
 * 
 * Implements proper consent mode for:
 * - Google (GA4 + Google Ads) via Google Consent Mode v2
 * - Meta Pixel with consent API
 * - Microsoft Clarity
 * - TikTok Pixel
 * 
 * Scripts are only loaded AFTER user has given explicit consent.
 */

// Script IDs - Replace these with actual IDs when configuring
const GOOGLE_TAG_ID = ''; // e.g., 'G-XXXXXXXXXX' or 'AW-XXXXXXXXXX'
const META_PIXEL_ID = ''; // e.g., '1234567890'
const CLARITY_PROJECT_ID = ''; // e.g., 'abc123xyz'
const TIKTOK_PIXEL_ID = ''; // e.g., 'XXXXXXXXXX'

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    fbq: (...args: unknown[]) => void;
    _fbq: unknown;
    clarity: (...args: unknown[]) => void;
    ttq: {
      load: (id: string) => void;
      page: () => void;
      track: (...args: unknown[]) => void;
      identify: (...args: unknown[]) => void;
      instances: unknown[];
      _i: unknown[];
    };
  }
}

// Initialize Google Consent Mode with default denied state
function initGoogleConsentMode() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  
  // Set default consent to denied
  window.gtag('consent', 'default', {
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'analytics_storage': 'denied',
    'wait_for_update': 500,
  });
}

// Update Google consent based on user preferences
function updateGoogleConsent(analytics: boolean, marketing: boolean) {
  if (typeof window.gtag !== 'function') return;
  
  window.gtag('consent', 'update', {
    'analytics_storage': analytics ? 'granted' : 'denied',
    'ad_storage': marketing ? 'granted' : 'denied',
    'ad_user_data': marketing ? 'granted' : 'denied',
    'ad_personalization': marketing ? 'granted' : 'denied',
  });
}

// Load Google Tag (gtag.js)
function loadGoogleTag() {
  if (!GOOGLE_TAG_ID || document.getElementById('google-tag-script')) return;
  
  const script = document.createElement('script');
  script.id = 'google-tag-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TAG_ID}`;
  document.head.appendChild(script);
  
  script.onload = () => {
    window.gtag('js', new Date());
    window.gtag('config', GOOGLE_TAG_ID);
  };
}

// Initialize Meta Pixel with consent revoked
function initMetaPixel() {
  if (!META_PIXEL_ID) return;
  
  // Meta pixel will be fully initialized when the script loads
  // Just set up window._fbq for queueing
  if (!window._fbq) {
    window._fbq = [];
  }
}

// Load Meta Pixel script
function loadMetaPixel() {
  if (!META_PIXEL_ID || document.getElementById('meta-pixel-script')) return;
  
  const script = document.createElement('script');
  script.id = 'meta-pixel-script';
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  
  script.onload = () => {
    window.fbq('consent', 'grant');
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
  };
}

// Load Microsoft Clarity
function loadClarity() {
  if (!CLARITY_PROJECT_ID || document.getElementById('clarity-script')) return;
  
  // Initialize clarity queue
  if (!window.clarity) {
    const clarityQueue: unknown[][] = [];
    window.clarity = function(...args: unknown[]) {
      clarityQueue.push(args);
    };
    (window.clarity as unknown as { q: unknown[][] }).q = clarityQueue;
  }
  
  const script = document.createElement('script');
  script.id = 'clarity-script';
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
  document.head.appendChild(script);
}

// Load TikTok Pixel
function loadTikTokPixel() {
  if (!TIKTOK_PIXEL_ID || document.getElementById('tiktok-pixel-script')) return;
  
  // Initialize ttq
  window.ttq = window.ttq || { 
    _i: [], 
    instances: [],
    load: function(id: string) { this._i.push(['load', id]); },
    page: function() { this._i.push(['page']); },
    track: function(...args: unknown[]) { this._i.push(['track', ...args]); },
    identify: function(...args: unknown[]) { this._i.push(['identify', ...args]); },
  };
  
  const script = document.createElement('script');
  script.id = 'tiktok-pixel-script';
  script.async = true;
  script.src = 'https://analytics.tiktok.com/i18n/pixel/events.js';
  document.head.appendChild(script);
  
  script.onload = () => {
    window.ttq.load(TIKTOK_PIXEL_ID);
    window.ttq.page();
  };
}

// Remove scripts and try to clean up
function removeScript(scriptId: string) {
  const script = document.getElementById(scriptId);
  if (script) {
    script.remove();
  }
}

const CookieScriptLoader = memo(function CookieScriptLoader() {
  const { consent } = useCookieConsent();
  const hasInitialized = useRef(false);
  
  // Initialize Google Consent Mode immediately (before any consent)
  useEffect(() => {
    if (!hasInitialized.current) {
      initGoogleConsentMode();
      initMetaPixel();
      hasInitialized.current = true;
    }
  }, []);
  
  // React to consent changes
  useEffect(() => {
    if (!consent) return;
    
    const { analytics, marketing } = consent;
    
    // Update Google Consent Mode
    updateGoogleConsent(analytics, marketing);
    
    // Load or cleanup scripts based on consent
    if (analytics) {
      loadGoogleTag();
      loadClarity();
    } else {
      removeScript('clarity-script');
    }
    
    if (marketing) {
      loadGoogleTag(); // Google Ads uses same tag
      loadMetaPixel();
      loadTikTokPixel();
    } else {
      removeScript('meta-pixel-script');
      removeScript('tiktok-pixel-script');
      // Revoke Meta consent if loaded
      if (typeof window.fbq === 'function') {
        window.fbq('consent', 'revoke');
      }
    }
  }, [consent]);
  
  // Listen for consent changes from other tabs/sources
  useEffect(() => {
    const handleConsentChange = (event: CustomEvent<CookieConsent>) => {
      const { analytics, marketing } = event.detail;
      updateGoogleConsent(analytics, marketing);
    };
    
    window.addEventListener('cookieConsentChanged', handleConsentChange as EventListener);
    return () => {
      window.removeEventListener('cookieConsentChanged', handleConsentChange as EventListener);
    };
  }, []);
  
  // This component doesn't render anything
  return null;
});

export default CookieScriptLoader;
